import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const Index = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const cameraGroupRef = useRef<THREE.Group>();
  const progressRef = useRef(0);
  const pathRef = useRef<THREE.CatmullRomCurve3>();
  const lightRef = useRef<THREE.PointLight>();

  useEffect(() => {
    if (!mountRef.current) return;

    // 设置渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);

    // 创建场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    sceneRef.current = scene;

    // 创建相机
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    cameraRef.current = camera;
    
    const cameraGroup = new THREE.Group();
    cameraGroup.position.z = 400;
    cameraGroup.add(camera);
    cameraGroupRef.current = cameraGroup;
    scene.add(cameraGroup);

    // 生成路径点
    const generatePathPoints = (count = 10, spacing = 25) => {
      const points = [];
      for (let i = 0; i < count; i++) {
        const x = i * spacing;
        const y = Math.sin(i * 0.5 + Math.random()) * 100;
        const z = Math.cos(i * 0.3 + Math.random()) * 100;
        points.push(new THREE.Vector3(x, z, y));
      }
      return points;
    };

    const points = generatePathPoints(10);
    const path = new THREE.CatmullRomCurve3(points);
    path.closed = true;
    pathRef.current = path;

    // 创建轨道环
    const ringCount = 600;
    const ringRadius = 3;
    const ringSegments = 32;
    const frenetFrames = path.computeFrenetFrames(ringCount, true);

    for (let i = 0; i <= ringCount; i++) {
      const t = i / ringCount;
      const pos = path.getPointAt(t);
      const normal = frenetFrames.normals[i];
      const binormal = frenetFrames.binormals[i];

      const ringPoints = [];
      for (let j = 0; j <= ringSegments; j++) {
        const theta = (j / ringSegments) * Math.PI * 2;
        const x = Math.cos(theta) * ringRadius;
        const y = Math.sin(theta) * ringRadius;
        const point = new THREE.Vector3().addVectors(
          pos,
          new THREE.Vector3()
            .addScaledVector(normal, x)
            .addScaledVector(binormal, y)
        );
        ringPoints.push(point);
      }

      const ringGeometry = new THREE.BufferGeometry().setFromPoints(ringPoints);

      // 创建渐变颜色
      const color = new THREE.Color();
      color.setHSL(t, 1.0, 0.5);

      const ringMaterial = new THREE.LineBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.8,
        depthWrite: false
      });

      const ring = new THREE.LineLoop(ringGeometry, ringMaterial);
      scene.add(ring);
    }

    // 添加光源
    const light = new THREE.PointLight(0xffffff, 0.3, 4);
    light.castShadow = true;
    lightRef.current = light;
    scene.add(light);

    // 更新相机位置
    const updateCamera = (t: number) => {
      if (!pathRef.current || !cameraGroupRef.current || !lightRef.current) return;
      
      const p1 = pathRef.current.getPointAt(t % 1);
      const p2 = pathRef.current.getPointAt((t + 0.01) % 1);
      cameraGroupRef.current.position.set(p1.x, p1.y, p1.z);
      cameraGroupRef.current.lookAt(p2);
      lightRef.current.position.set(p2.x, p2.y, p2.z);
    };

    // 动画循环
    const animate = () => {
      progressRef.current = (progressRef.current + 0.001) % 1;
      updateCamera(progressRef.current);
      
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      
      frameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // 处理窗口大小变化
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      window.removeEventListener('resize', handleResize);
      
      if (rendererRef.current && mountRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
      
      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            object.geometry.dispose();
            if (object.material instanceof THREE.Material) {
              object.material.dispose();
            }
          }
        });
      }
    };
  }, []);

  return (
    <div className="w-full h-screen bg-black overflow-hidden">
      <div ref={mountRef} className="w-full h-full" />
      
      {/* 控制说明 */}
      <div className="absolute top-4 left-4 text-white bg-black/50 p-4 rounded-lg backdrop-blur-sm">
        <h2 className="text-xl font-bold mb-2">渐变色飞行轨道</h2>
        <p className="text-sm opacity-80">体验沉浸式的3D彩色隧道飞行</p>
      </div>
      
      {/* 加载提示 */}
      <div className="absolute bottom-4 right-4 text-white bg-black/50 p-2 rounded-lg backdrop-blur-sm">
        <p className="text-xs opacity-80">使用 Three.js 渲染</p>
      </div>
    </div>
  );
};

export default Index;